// ─────────────────────────────────────────────────────────────────────────────
// Frise salariale « au fil des années » — fonction pure (testable, sans Firestore).
//
// Reconstruit une série annuelle salaire/échelon à partir de :
//  - le PASSÉ réel : salary_history (`pastPoints`, résolu par l'appelant), en
//    repli le prorata des taux réels datés (rate_history) de l'année ;
//  - le PRÉSENT : le RÉEL prime — prorata des taux datés de l'année courante ;
//    sans taux réel, repli grille (classe/échelon) ;
//  - le FUTUR : projection du POTENTIEL (montée d'un échelon/an jusqu'au plafond
//    du niveau, puis aug_grille_pct), ANCRÉE sur le réel : la trajectoire de
//    grille est mise à l'échelle pour partir du taux réel en vigueur — courbe
//    continue, pas de rupture (décision « partir du réel et le faire croître »).
//
// Remise à zéro 2026 : les rôles/échelons passés sont une structure DISTINCTE de
// la grille 2026 — aucun parallèle d'échelon, le passé vient des grilles réelles
// de chaque année via `pastPoints`.
// ─────────────────────────────────────────────────────────────────────────────

import { annualRealPayroll, daysInYear } from './payroll'
import { annualFromHourly, projectYearRate, round2, type HoursParams } from './projection'
import type { SalaryClassEchelon } from '../types/firestore'

export const GRID_YEAR = 2026 // année de référence des taux de la grille master
const MAX_LOOKBACK = 5 // 5 ans avant l'année courante
const DEFAULT_YEARS_AHEAD = 5 // 5 ans de potentiel futur

export type SalaryPointKind = 'history' | 'current' | 'projection'

// Point passé réel pré-résolu par l'appelant, depuis l'historique salarial réel
// (pulse_compensation/{email}/salary_history) : rôle d'époque + salaire annuel
// réellement versé. L'échelon est optionnel (le passé pré-2026 n'a pas de
// correspondance avec les échelons de la grille courante).
export type PastSalaryPoint = {
  year: number
  echelon?: number | null
  hourly?: number | null
  annual: number | null
  roleLabel: string | null
}

export type SalaryPoint = {
  year: number
  echelon: number
  hourly: number | null
  annual: number | null
  // Taux RÉEL en vigueur au 31 décembre de l'année (rate_history) — null si
  // aucun taux saisi ou si le suivi des taux réels n'est pas alimenté.
  realHourly: number | null
  realAnnual: number | null
  // Rôle (libellé de classe) — renseigné pour le passé réel, null sinon.
  roleLabel: string | null
  kind: SalaryPointKind
  isChange: boolean // une montée d'échelon a pris effet cette année (passé réel ou potentiel futur)
  isRoleChange: boolean // le rôle a changé par rapport à l'année précédente (passé)
  isHire: boolean // année d'embauche
}

export type SalaryTimeline = {
  points: SalaryPoint[]
  currentAnnual: number | null
  currentHourly: number | null
  currentEchelon: number
  maxEchelon: number
}

type RealRateChange = {
  hourly_rate: number
  effective_date: string // ISO 'YYYY-MM-DD'
}

export function buildSalaryTimeline(input: {
  currentEchelon: number
  hireDate: string | null
  echelons: SalaryClassEchelon[]
  augGrillePct: number | null
  hours: HoursParams
  // Passé réel pré-résolu (rôle + salaire des grilles de chaque année), résolu
  // par l'appelant car nécessite Firestore. Optionnel (= aucun passé connu).
  pastPoints?: PastSalaryPoint[]
  // Historique des taux réels datés (rate_history) — optionnel, rétrocompatible.
  realRates?: RealRateChange[]
  // Date de départ (fin d'emploi) — borne le prorata réel de l'année courante.
  departureDate?: string | null
  currentYear: number
  gridYear?: number
  yearsAhead?: number
  // Plafond d'avancement du niveau de l'employé (echelon_max) — borne la montée
  // potentielle future et le « échelon X / max » affiché. Optionnel (= toute l'échelle).
  maxEchelonCap?: number | null
}): SalaryTimeline {
  const {
    currentEchelon,
    hireDate,
    echelons,
    augGrillePct,
    hours,
    pastPoints = [],
    realRates = [],
    departureDate = null,
    currentYear,
    gridYear = GRID_YEAR,
    yearsAhead = DEFAULT_YEARS_AHEAD,
    maxEchelonCap = null,
  } = input

  const hireYear = hireDate ? Number(hireDate.slice(0, 4)) : null
  const cap = maxEchelonCap ?? echelons.length

  // Échelon affiché présent/futur : courant exact à l'année courante ; pour le
  // futur, montée d'un échelon par an jusqu'au plafond du niveau, puis grille seule.
  function echelonForYear(year: number): number {
    if (year <= currentYear) return currentEchelon
    return Math.min(currentEchelon + (year - currentYear), cap)
  }

  // Taux grille (présent/futur) projeté via aug_grille_pct depuis l'année de réf.
  function rateFor(echelon: number, year: number): number | null {
    const e = echelons.find((x) => x.echelon === echelon)
    if (!e) return null
    return projectYearRate(e.rate, year - gridYear, augGrillePct)
  }

  // Taux réel en vigueur au 31 décembre d'une année (dernier changement daté
  // ≤ 31 déc). null avant le premier taux connu ou pour les années futures.
  const sortedRealRates = [...realRates].sort((a, b) =>
    a.effective_date.localeCompare(b.effective_date),
  )
  function realRateAt(year: number): number | null {
    if (year > currentYear) return null // pas de projection du taux réel
    let rate: number | null = null
    for (const r of sortedRealRates) {
      if (r.effective_date <= `${year}-12-31`) rate = r.hourly_rate
    }
    return rate
  }

  // Salaire annuel RÉEL d'une année : prorata des taux datés (fenêtre embauche/
  // départ), même moteur que la masse salariale. AFFICHAGE : les jours
  // antérieurs au premier taux connu sont valorisés au premier taux (un
  // historique qui démarre en cours d'année afficherait sinon un demi-salaire).
  // La masse salariale, elle, garde la couverture stricte (real-payroll.ts).
  function realAnnualForYear(year: number): number | null {
    if (sortedRealRates.length === 0) return null
    const p = annualRealPayroll({
      year,
      rates: sortedRealRates,
      weeklyHours: hours.base_hours_per_week,
      weeksPerYear: hours.weeks_per_year,
      hireDate,
      departureDate,
    })
    if (p.unpriced) return null
    const uncovered = p.windowDays - p.coverageDays
    const firstRate = p.segments[0]?.hourly_rate ?? null
    if (uncovered <= 0 || firstRate === null) return Math.round(p.total)
    const padding =
      firstRate *
      hours.base_hours_per_week *
      hours.weeks_per_year *
      (uncovered / daysInYear(year))
    return Math.round(p.total + padding)
  }

  // Ancrage de la projection sur le RÉEL : la trajectoire de grille future est
  // mise à l'échelle pour partir du taux réel en vigueur en fin d'année courante
  // (le taux avec lequel on aborde l'an prochain — pas le prorata mélangé de
  // l'année). Sans taux réel, ou sans taux grille à l'échelon courant, échelle 1
  // (grille pure — comportement d'origine).
  const realCurrentRate = realRateAt(currentYear)
  const gridCurrentRate = rateFor(currentEchelon, currentYear)
  const projectionScale =
    realCurrentRate !== null && gridCurrentRate !== null && gridCurrentRate > 0
      ? realCurrentRate / gridCurrentRate
      : 1

  const pastByYear = new Map(pastPoints.map((p) => [p.year, p]))

  // Plage d'années affichée : bornée à 5 ans en arrière / yearsAhead en avant.
  const starts = [currentYear, ...pastPoints.map((p) => p.year)]
  if (hireYear && Number.isFinite(hireYear)) starts.push(hireYear)
  let startYear = Math.min(...starts)
  startYear = Math.max(startYear, currentYear - MAX_LOOKBACK)
  const endYear = currentYear + yearsAhead

  const points: SalaryPoint[] = []
  let prevPast: PastSalaryPoint | null = null
  for (let year = startYear; year <= endYear; year++) {
    const realRate = realRateAt(year)
    const realHourly = realRate === null ? null : round2(realRate)
    const realAnnual = realRate === null ? null : annualFromHourly(realRate, hours)

    if (year < currentYear) {
      // Passé réel : salary_history (pastPoints) d'abord — le « réellement
      // versé » (primes incluses) prime ; en repli, le prorata des taux réels
      // datés de l'année (rate_history) — l'évolution réelle reste tracée même
      // sans import d'historique salarial.
      const pp = pastByYear.get(year) ?? null
      const isRoleChange = pp != null && prevPast != null && pp.roleLabel !== prevPast.roleLabel
      const isChange = pp != null && prevPast != null && pp.echelon !== prevPast.echelon
      points.push({
        year,
        echelon: pp?.echelon ?? 0,
        hourly: pp?.hourly ?? realHourly,
        annual: pp?.annual ?? realAnnualForYear(year),
        realHourly,
        realAnnual,
        roleLabel: pp?.roleLabel ?? null,
        kind: 'history',
        isChange: isChange || year === hireYear,
        isRoleChange,
        isHire: year === hireYear,
      })
      if (pp) prevPast = pp
      continue
    }

    // Présent : le RÉEL prime (prorata des taux datés de l'année) ; repli
    // grille sans taux réel. Futur : potentiel grille (borné au plafond du
    // niveau) × projectionScale — ancré sur le réel, courbe continue.
    const echelon = echelonForYear(year)
    const rate = rateFor(echelon, year)
    let hourly: number | null
    let annual: number | null
    if (year === currentYear && realCurrentRate !== null) {
      hourly = round2(realCurrentRate)
      annual = realAnnualForYear(year) ?? annualFromHourly(realCurrentRate, hours)
    } else {
      const scaled = rate === null ? null : rate * (year > currentYear ? projectionScale : 1)
      hourly = scaled === null ? null : round2(scaled)
      annual = scaled === null ? null : annualFromHourly(scaled, hours)
    }
    points.push({
      year,
      echelon,
      hourly,
      annual,
      realHourly,
      realAnnual,
      roleLabel: null,
      kind: year === currentYear ? 'current' : 'projection',
      // Montée potentielle : l'échelon a progressé vs l'année précédente.
      isChange: year > currentYear && echelon > echelonForYear(year - 1),
      isRoleChange: false,
      isHire: year === hireYear,
    })
  }

  const currentPoint = points.find((p) => p.year === currentYear) ?? null

  return {
    points,
    currentAnnual: currentPoint?.annual ?? null,
    currentHourly: currentPoint?.hourly ?? null,
    currentEchelon,
    maxEchelon: cap,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Frise AGRÉGÉE d'équipe — masse salariale totale au fil des années.
//
// Somme, année par année, des frises individuelles (une par membre). Sert la vue
// directeur « mode graphique » : une seule courbe (total équipe), passé réel en
// trait plein → projection potentielle en pointillé, réutilisée telle quelle par
// SalaryProgressionChart. Aucune donnée individuelle n'est exposée — uniquement
// les totaux annuels.
// ─────────────────────────────────────────────────────────────────────────────

// Entrées par membre, déjà filtrées « priced » par l'appelant (classe + échelon
// valides). realRates : mêmes taux datés que la vue individuelle — le point de
// l'année courante de la courbe d'équipe suit alors le RÉEL (cohérent avec le
// tableau « masse réelle » de la même page), et la projection est ancrée dessus.
export type TeamSalaryMemberInput = {
  currentEchelon: number
  hireDate: string | null
  echelons: SalaryClassEchelon[]
  augGrillePct: number | null
  hours: HoursParams
  pastPoints?: PastSalaryPoint[]
  realRates?: RealRateChange[]
  departureDate?: string | null
  maxEchelonCap?: number | null
}

export function buildTeamSalaryTimeline(input: {
  members: TeamSalaryMemberInput[]
  currentYear: number
  gridYear?: number
  yearsAhead?: number
}): SalaryTimeline {
  const { members, currentYear, gridYear = GRID_YEAR, yearsAhead = DEFAULT_YEARS_AHEAD } = input

  // Frise individuelle de chaque membre (mêmes paramètres que la vue profil).
  const timelines = members.map((m) =>
    buildSalaryTimeline({
      currentEchelon: m.currentEchelon,
      hireDate: m.hireDate,
      echelons: m.echelons,
      augGrillePct: m.augGrillePct,
      hours: m.hours,
      pastPoints: m.pastPoints,
      realRates: m.realRates,
      departureDate: m.departureDate ?? null,
      maxEchelonCap: m.maxEchelonCap ?? null,
      currentYear,
      gridYear,
      yearsAhead,
    }),
  )

  // Plage d'années = union de toutes les frises (robuste aux embauches récentes).
  const years = new Set<number>()
  for (const t of timelines) for (const p of t.points) years.add(p.year)
  const sortedYears = [...years].sort((a, b) => a - b)

  // Somme des `annual` non-null par année : présent/futur toujours complet
  // (projection grille) ; passé = somme des historiques saisis seulement, donc
  // potentiellement partiel ou absent (aucun historique cette année-là ⇒ null).
  const points: SalaryPoint[] = sortedYears.map((year) => {
    let sum = 0
    let hasValue = false
    for (const t of timelines) {
      const p = t.points.find((x) => x.year === year)
      if (p && p.annual !== null) {
        sum += p.annual
        hasValue = true
      }
    }
    return {
      year,
      echelon: 0,
      hourly: null,
      annual: hasValue ? round2(sum) : null,
      realHourly: null,
      realAnnual: null,
      roleLabel: null,
      kind: year < currentYear ? 'history' : year === currentYear ? 'current' : 'projection',
      isChange: false,
      isRoleChange: false,
      isHire: false,
    }
  })

  const currentPoint = points.find((p) => p.year === currentYear) ?? null

  return {
    points,
    currentAnnual: currentPoint?.annual ?? null,
    currentHourly: null,
    currentEchelon: 0,
    maxEchelon: 0,
  }
}
